import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { format, subDays } from "date-fns";

interface AiUsageDayBucket {
  date: string;
  totalRub: number;
  totalUsd: number;
  totalTokens: number;
  callsByKind: Record<string, number>;
}

interface AiUsageRow {
  id: number;
  taskId: string;
  requestKind: string;
  modelResolved: string | null;
  totalTokens: number | null;
  totalUsd: number | null;
  totalRub: number | null;
  signCode: string | null;
  createdAt: string;
}

export function AiCost() {
  const from = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const to = format(new Date(), "yyyy-MM-dd");

  // /api/usage/calls возвращает { from, to, count, items: AiUsageRow[] }
  interface CallsResponse {
    from: string;
    to: string;
    count: number;
    items: AiUsageRow[];
  }
  const usage = useQuery({
    queryKey: ["ai-usage", from, to],
    queryFn: async () => {
      const r = await api<CallsResponse>(`/api/usage/calls?from=${from}&to=${to}`);
      return r.items;
    },
    retry: false,
  });

  // Группировка по дням
  const buckets = new Map<string, AiUsageDayBucket>();
  for (const r of usage.data ?? []) {
    const date = (r.createdAt ?? "").slice(0, 10);
    if (!date) continue;
    const b = buckets.get(date) ?? { date, totalRub: 0, totalUsd: 0, totalTokens: 0, callsByKind: {} };
    b.totalRub += r.totalRub ?? 0;
    b.totalUsd += r.totalUsd ?? 0;
    b.totalTokens += r.totalTokens ?? 0;
    b.callsByKind[r.requestKind] = (b.callsByKind[r.requestKind] ?? 0) + 1;
    buckets.set(date, b);
  }
  const series = Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));

  const totalRub = series.reduce((s, b) => s + b.totalRub, 0);
  const totalUsd = series.reduce((s, b) => s + b.totalUsd, 0);
  const totalCalls = (usage.data ?? []).length;

  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-bold">AI расходы</h1>

      {usage.isError && (
        <div className="card p-6 bg-amber-500/5 border-amber-500/20">
          <p className="text-amber-200">
            Endpoint /api/ai-usage пока не реализован в worker'е. Это TODO для следующей итерации.
          </p>
          <p className="text-white/50 text-sm mt-2">
            Пока — посмотри прямо в БД через psql:
            <code className="block mt-2 bg-black/40 rounded px-3 py-2 font-mono text-xs">
              docker exec astrov2-postgres psql -h 127.0.0.1 -p 15432 -U astrov2 -d astrov2 -c "select request_kind, count(*), sum(total_rub) from ai_usage_log group by 1"
            </code>
          </p>
        </div>
      )}

      {usage.data && (
        <>
          <section className="grid grid-cols-3 gap-4">
            <Stat label="Вызовов AI" value={totalCalls.toString()} />
            <Stat label="Сумма USD" value={`$${totalUsd.toFixed(2)}`} />
            <Stat label="Сумма ₽" value={`₽${totalRub.toFixed(2)}`} />
          </section>

          <section className="card p-6">
            <h2 className="font-display text-xl font-semibold mb-4">По дням</h2>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" />
                <YAxis stroke="rgba(255,255,255,0.4)" />
                <Tooltip contentStyle={{ background: "#1a1d2e", border: "1px solid rgba(255,255,255,0.1)" }} />
                <Line type="monotone" dataKey="totalRub" stroke="#6c5dd3" strokeWidth={2} name="₽" />
              </LineChart>
            </ResponsiveContainer>
          </section>

          <section className="card p-6">
            <h2 className="font-display text-xl font-semibold mb-4">Кол-во вызовов по типам</h2>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" />
                <YAxis stroke="rgba(255,255,255,0.4)" />
                <Tooltip contentStyle={{ background: "#1a1d2e", border: "1px solid rgba(255,255,255,0.1)" }} />
                <Legend />
                <Bar dataKey={(b: AiUsageDayBucket) => b.callsByKind.chat ?? 0} fill="#6c5dd3" name="chat" />
                <Bar dataKey={(b: AiUsageDayBucket) => b.callsByKind.image ?? 0} fill="#f4c443" name="image" />
              </BarChart>
            </ResponsiveContainer>
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-5">
      <div className="text-white/50 text-sm mb-2">{label}</div>
      <div className="font-display text-3xl font-bold">{value}</div>
    </div>
  );
}
